import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { RollingText } from '../../../components/ui/RollingText';
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef(null);
  const frameRef = useRef(null);
  const logoRef = useRef(null);
  const contentRef = useRef(null);
  const imageScaleRef = useRef(null); // FIX: wrapper div se animira, blur na <img> ostaje statičan
  const loaderRef = useRef(null);
  const hexagonImageRef = useRef(null);

  const [theme, setTheme] = useState(themeManager.getCurrentTheme());

  useEffect(() => {
    const handleThemeChange = (event) => setTheme(event.detail.theme);
    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const logoEl       = logoRef.current;
      const contentEl    = contentRef.current;
      const imageScaleEl = imageScaleRef.current;
      const loaderEl     = loaderRef.current;
      const frameEl      = frameRef.current;
      const hexagonEl    = hexagonImageRef.current;

      if (logoEl)       gsap.set(logoEl,       { autoAlpha: 0, scale: 0.95 });
      if (contentEl)    gsap.set(contentEl,    { autoAlpha: 0, y: 30 });
      if (imageScaleEl) gsap.set(imageScaleEl, { scale: 1.2 });
      if (loaderEl)     gsap.set(loaderEl,     { yPercent: 0 });
      if (frameEl)      gsap.set(frameEl,      { scale: 0.98, autoAlpha: 0 });

      if (hexagonEl) {
        gsap.set(hexagonEl, {
          attr: { opacity: 1, x: 443, y: 155, width: 50, height: 44 },
        });
      }

      const hasTimelineTargets = loaderEl || frameEl || logoEl || contentEl || hexagonEl;
      if (!hasTimelineTargets) return;

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // --- 1. LOADER EXIT ---
      if (loaderEl) {
        tl.to(loaderEl, {
          yPercent: -100,
          duration: 1.2,
          ease: 'power4.inOut',
        });
      }

      // --- 2. FRAME & IMAGE REVEAL ---
      if (frameEl) {
        tl.to(frameEl, { autoAlpha: 1, scale: 1, duration: 1 }, '-=0.4');
      }
      if (imageScaleEl) {
        tl.to(imageScaleEl, { scale: 1, duration: 1.5, ease: 'power2.out' }, '-=0.8');
      }
      if (logoEl) {
        tl.to(logoEl, { autoAlpha: 1, scale: 1, duration: 1.2 }, '-=1');
      }
      if (contentEl) {
        tl.to(contentEl, { y: 0, autoAlpha: 1, duration: 0.8 }, '-=0.8');
      }

      // --- 3. HEXAGON LOGO ANIMATION ---
      if (hexagonEl) {
        // 3a. Slide ka centru (bez povećanja)
        tl.to(hexagonEl, {
          attr: { x: 586, y: 155 },
          duration: 1.0,
          ease: 'power2.out',
        }, '-=0.8');

        // 3b. Expand na finalnu veličinu
        tl.to(hexagonEl, {
          attr: { width: 393, height: 347, x: 656, y: 171 },
          duration: 0.8,
          ease: 'power2.out',
        });
      }

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const [imagePath, setImagePath] = useState('/assets/images/pc-hero-wide.webp');

  useEffect(() => {
    const updateImagePath = () => {
      setImagePath(
        window.innerWidth < 768
          ? '/assets/images/pc-hero-wide-mobile.webp'
          : '/assets/images/pc-hero-wide.webp'
      );
    };
    updateImagePath();
    window.addEventListener('resize', updateImagePath);
    return () => window.removeEventListener('resize', updateImagePath);
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`relative h-[calc(100vh-92px)] w-full overflow-hidden ${
        theme === 'dark' ? 'bg-[#0F1115]' : 'bg-[#F3F4F6]'
      } flex items-center justify-center`}
      style={{ contain: 'paint' }}
    >

      {/* LOADER
      <div ref={loaderRef} className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0b0b0e]">
        <div className="w-px h-20 bg-white/20 overflow-hidden relative">
          <div className="absolute inset-0 bg-white animate-[bounce_2s_infinite]"></div>
        </div>
      </div> */}

      <div
        ref={frameRef}
        className="relative w-[98%] h-[100%] bg-[#0F1115] rounded-[46px] overflow-hidden"
      >

        {/* FIX: GSAP animira ovaj wrapper div (scale 1.2→1), a ne <img> direktno.
            blur() na <img> ostaje statičan → nema per-frame CSS filter repaint */}
        <div ref={imageScaleRef} className="absolute inset-0">
          <img
            src={imagePath}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.3) blur(8px)' }}
          />
        </div>

        <div className="relative z-10 flex h-full items-center justify-center px-4">
          <svg
            ref={logoRef}
            className="w-full max-w-4xl drop-shadow-[0_0_30px_rgba(0,0,0,0.5)]"
            viewBox="0 0 1311 1051"
            style={{ transformOrigin: 'center' }}
          >
            <defs>
              <clipPath id="logoClip">
                <path d="M72.8 787c-34 0-54.4-18.8-54.4-51.6v-146c0-33.2 20.8-53.2 54-53.2H190v45.2H89.6c-16.4 0-24 8.4-24 22v116.8c0 14.8 8.4 21.6 22.8 21.6H190V787zm152.725 0V536.2h178v45.2h-129.6v54h113.6V681h-113.6v60.8h129.6V787zm361.672-250.8h47.6V787h-42.8l-105.6-160.8V787h-47.2V536.2h44.4l104.8 160.4zM737.041 787V581.4h-67.2v-45.2h186v45.2h-70.8V787zm279.759 0-15.6-46.8h-91.197l-15.6 46.8h-50.8l84.8-250.8h54.8L1068 787zm-91.997-92.4h61.6l-30.8-93.2zm367.327-55.2c0 29.6-14.01 48-40.4 51.2l39.6 96.4h-48.8l-40-95.6h-54V787h-48V536.2h136c34.4 0 55.6 16 55.6 48.8zm-143.6-58v65.2h76.8c12.4 0 19.2-6 19.2-18.4v-28.8c0-12-7.6-18-19.2-18zM132.88 499V198.04h168.96c38.4 0 60.96 24 60.96 63.36V331c0 42.24-21.12 64.8-64.32 64.8h-108V499zm57.6-158.88h91.2c17.28 0 24-7.68 24-21.6v-44.64c0-13.44-10.56-21.6-24-21.6h-91.2zM472.266 499c-40.8 0-65.28-22.56-65.28-61.92v-175.2c0-39.84 24.96-63.84 64.8-63.84h141.12v54.24h-120.48c-19.68 0-28.8 10.08-28.8 26.4v140.16c0 17.76 10.08 25.92 27.36 25.92h121.92V499zM133 498h57v39h-57z" />
              </clipPath>
            </defs>
            <image
              href={imagePath}
              width="100%"
              height="100%"
              preserveAspectRatio="xMidYMid slice"
              clipPath="url(#logoClip)"
            />
            <image
              ref={hexagonImageRef}
              href="/assets/images/hero-logo2-removebg-preview.webp"
              x="1250"
              y="50"
              width="393"
              height="347"
              style={{ transformOrigin: 'center', transformBox: 'fill-box' }}
            />
          </svg>
        </div>

        <div
          ref={contentRef}
          className="absolute bottom-20 z-20 w-full text-center text-white md:bottom-20 sm:absolute sm:bottom-auto sm:translate-y-0 sm:pt-2"
        >
          <RollingText
            text={t('hero.subtitle') || 'Vodeći distributer IT opreme sa 33+ godina iskustva'}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] sm:tracking-[0.5em] opacity-70 block whitespace-nowrap px-2"
            repeatInterval={4000}
          />
        </div>

      </div>

    </section>
  );
};

export default HeroSection;